import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { NotificationItem } from '@/features/notifications/notification-item';
import { useMarkRead, useNotifications, useUnreadCount } from '@/hooks/use-notifications';

const PREVIEW = 6;

/** Дзвіночок із лічильником непрочитаних (спека Dashboard §2.1) і коротким списком. */
export function NotificationsBell(): ReactNode {
	const [open, setOpen] = useState(false);
	const container = useRef<HTMLDivElement>(null);
	const unread = useUnreadCount();
	const { notifications } = useNotifications();
	const { markRead, markAllRead } = useMarkRead();

	useEffect(() => {
		if (!open) return;
		
		const onPointerDown = (e: PointerEvent): void => {
			if (!container.current?.contains(e.target as Node)) setOpen(false);
		};
		const onKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return (): void => {
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	return (
		<div ref={container} className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
				aria-expanded={open}
				className="relative grid size-10 place-items-center rounded-control text-fg transition-colors hover:bg-surface-muted"
			>
				<Bell className="size-5" strokeWidth={1.8} />
				{unread > 0 && (
					<span
						data-testid="unread-count"
						className="absolute right-0.5 top-1 min-w-4.25 rounded-full border-2 border-bg bg-primary-solid px-1 text-center text-[0.625rem] font-bold leading-3.25 tabular-nums text-primary-fg"
					>
						{unread > 99 ? '99+' : unread}
					</span>
				)}
			</button>

			{open && (
				<div className="absolute left-0 top-full z-50 mt-2 w-88 rounded-card border border-border bg-surface p-2 shadow-e3 lg:left-auto lg:right-0">
					<div className="flex items-center justify-between px-2 py-1.5">
						<p className="text-sm font-medium">Notifications</p>
						{unread > 0 && (
							<button type="button" onClick={markAllRead} className="text-xs text-primary hover:underline">
								Mark all as read
							</button>
						)}
					</div>
					{notifications.length === 0 ? (
						<p className="px-2 py-6 text-center text-sm text-fg-muted">Nothing here yet.</p>
					) : (
						<ul className="max-h-96 space-y-0.5 overflow-y-auto">
							{notifications.slice(0, PREVIEW).map((n) => (
								<li key={n.id}>
									<NotificationItem
										notification={n}
										compact
										onOpen={(id) => {
											markRead(id);
											setOpen(false);
										}}
									/>
								</li>
							))}
						</ul>
					)}
					<Link
						to="/notifications"
						onClick={() => setOpen(false)}
						className="mt-1 block rounded-control px-2 py-2 text-center text-sm font-medium text-primary hover:bg-surface-muted"
					>
						View all
					</Link>
				</div>
			)}
		</div>
	);
}
